import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace';
import {
  VendorUploadPage,
  VendorUploadSessionSchema,
  type VendorUploadSession,
} from '@/components/vendor-knowledge/upload/vendor-upload-page';
import {
  SecureLinkSessionSchema,
  getSecureLinkSession,
  type SecureLinkSession,
} from '@/lib/api/secure-links';
import { ClientCard, ClientFrame } from './client-frame';
import { ClientMapValidation } from './client-map-validation';
import { ClientQuestionnaire } from './client-questionnaire';
import { ClientSignoff } from './client-signoff';
import { LinkInvalidCard, isLinkRefused, problemText } from './secure-link-parts';

/**
 * The entry of `/l#<token>`. The token rides the URL fragment, which
 * browsers never send, so it never reaches a server log. It is read once
 * and taken out of the address bar and the history entry; from then on it
 * goes only in the `X-Caliber-Link` header. The tab keeps it in session
 * storage, so a reload still works; it is gone when the tab closes, and
 * dropped as soon as the link is refused.
 */
export function SecureLinkEntry() {
  const [token, setToken] = React.useState(() => readLinkFragment() ?? recallLinkToken());

  React.useEffect(() => {
    document.title = 'Avero Advisors';
    if (token) rememberLinkToken(token);
    clearLinkFragment();
    // Another link opened in the same tab changes only the fragment.
    const onHash = () => {
      const next = readLinkFragment();
      if (!next) return;
      rememberLinkToken(next);
      clearLinkFragment();
      setToken(next);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // Runs once, for the first token; later ones arrive through `hashchange`.
  }, []);

  return <SecureLinkPage key={token ?? 'none'} token={token} />;
}

const TOKEN_KEY = 'caliber.secure-link';

/** The token in the fragment, if any. Reading changes nothing. */
function readLinkFragment(): string | null {
  const token = window.location.hash.replace(/^#/, '').trim();
  return token.length > 0 ? token : null;
}

/** The same address and history entry, without the fragment. */
function clearLinkFragment(): void {
  if (!window.location.hash) return;
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search,
  );
}

function recallLinkToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function rememberLinkToken(token: string): void {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage off (a private window): the page still works until reloaded.
  }
}

function forgetLinkToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing kept, nothing to drop.
  }
}

/**
 * A tab left open is read again when it comes back into view (a link may
 * have been answered, replaced or revoked since), at most this often: every
 * read of the session counts as an open of the link.
 */
export const SESSION_RECHECK_MS = 30_000;

type Loaded =
  | { kind: 'loading' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'discovery'; session: SecureLinkSession }
  | { kind: 'vendor_upload'; session: VendorUploadSession }
  | { kind: 'other' };

/**
 * The page behind a secure link (`/l#<token>`): one purpose per link, no
 * account, and nothing beyond that purpose (rule 16). Discovery's client
 * pages (questionnaire, map validation, sign-off; T-0191) and the vendor
 * upload (T-0207) share it. Any refusal, on load or later, shows the same
 * "This link isn't valid any more" page, whatever the purpose.
 */
export function SecureLinkPage({ token }: { token: string | null }) {
  const [loaded, setLoaded] = React.useState<Loaded>({ kind: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  const [refused, setRefused] = React.useState(false);
  // The map page is wide; its thank-you is not.
  const [mapAnswered, setMapAnswered] = React.useState(false);
  const lastRead = React.useRef(0);

  React.useEffect(() => {
    if (token === null) return;
    let live = true;
    setLoaded({ kind: 'loading' });
    lastRead.current = Date.now();
    getSecureLinkSession(token)
      .then((raw) => {
        if (!live) return;
        if (raw.purpose === 'vendor_upload') {
          setLoaded({ kind: 'vendor_upload', session: VendorUploadSessionSchema.parse(raw) });
        } else if (DISCOVERY_PURPOSES.has(raw.purpose)) {
          setLoaded({ kind: 'discovery', session: SecureLinkSessionSchema.parse(raw) });
        } else {
          setLoaded({ kind: 'other' });
        }
      })
      .catch((error: unknown) => live && setLoaded({ kind: 'failed', error }));
    return () => {
      live = false;
    };
  }, [token, attempt]);

  const onInvalid = React.useCallback((err: unknown) => {
    if (!isLinkRefused(err)) return false;
    setRefused(true);
    return true;
  }, []);

  // A Discovery page shown in a tab that comes back into view is read again,
  // so a link replaced or answered meanwhile stops offering its form. The
  // page stays as it is unless the session changed; a refusal shows the
  // invalid page; any other failure leaves it alone.
  const showing = loaded.kind === 'discovery' && !refused;
  React.useEffect(() => {
    if (token === null || !showing) return;
    let live = true;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastRead.current < SESSION_RECHECK_MS) return;
      lastRead.current = Date.now();
      getSecureLinkSession(token)
        .then((raw) => {
          if (!live) return;
          const next = SecureLinkSessionSchema.safeParse(raw);
          if (!next.success) return;
          setLoaded((prev) =>
            prev.kind === 'discovery' && JSON.stringify(prev.session) === JSON.stringify(next.data)
              ? prev
              : { kind: 'discovery', session: next.data },
          );
        })
        .catch((err: unknown) => {
          if (live) onInvalid(err);
        });
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      live = false;
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [token, showing, onInvalid]);

  const invalid =
    token === null || refused || (loaded.kind === 'failed' && isLinkRefused(loaded.error));
  React.useEffect(() => {
    if (invalid) forgetLinkToken();
  }, [invalid]);

  React.useEffect(() => {
    document.title =
      loaded.kind === 'vendor_upload' && !invalid
        ? 'Documents for Avero Advisors'
        : 'Avero Advisors';
  }, [loaded.kind, invalid]);

  // The vendor upload page has its own frame (T-0207).
  if (!invalid && loaded.kind === 'vendor_upload' && token !== null) {
    return <VendorUploadPage token={token} session={loaded.session} />;
  }

  const data = loaded.kind === 'discovery' ? loaded.session : null;
  let body: React.ReactNode;
  if (invalid) {
    body = <LinkInvalidCard />;
  } else if (loaded.kind === 'failed') {
    body = (
      <ClientCard>
        <p className="text-body text-ink-700">
          {problemText(loaded.error, 'This page could not be loaded.')}
        </p>
        <WorkspaceButton size="md" className="mt-4" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </WorkspaceButton>
      </ClientCard>
    );
  } else if (loaded.kind === 'other') {
    body = (
      <ClientCard>
        <h1 className="mb-1 text-h1 font-semibold text-ink-900">This page is not ready yet</h1>
        <p className="text-body text-ink-700">
          Ask the person who sent the link. They can send it again when it is.
        </p>
      </ClientCard>
    );
  } else if (!data || token === null) {
    body = (
      <ClientCard>
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading…
        </div>
      </ClientCard>
    );
  } else if (data.purpose === 'questionnaire') {
    body = (
      <ClientQuestionnaire
        token={token}
        session={data}
        payload={data.payload}
        onInvalid={onInvalid}
      />
    );
  } else if (data.purpose === 'map_validation') {
    body = (
      <ClientMapValidation
        token={token}
        session={data}
        payload={data.payload}
        onInvalid={onInvalid}
        onAnswered={() => setMapAnswered(true)}
      />
    );
  } else {
    body = (
      <ClientSignoff token={token} session={data} payload={data.payload} onInvalid={onInvalid} />
    );
  }

  return (
    <main className="min-h-screen bg-paper-50">
      <ClientFrame
        forLine={
          data && !invalid
            ? `Secure link for ${data.client_name} · no account needed`
            : 'Secure link · no account needed'
        }
        wide={
          !invalid && !mapAnswered && data?.purpose === 'map_validation' && !data.payload.answer
        }
      >
        {body}
      </ClientFrame>
    </main>
  );
}

const DISCOVERY_PURPOSES = new Set(['questionnaire', 'map_validation', 'signoff']);
